import { useActiveBundle } from '@/context/active-bundle';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransition, TargetTransitionOptions } from '@/services/router/safe-navigate';
import type { MergedWorkspace } from '@/features/workspaces';
import { useFilterWorkspacesByName, useWorkspaceCount, useSampleWorkspace } from '@/features/workspaces';
import { AppBundle } from '@/utils/app-bundles';
import { workspaceUrlFromCurrentPath } from '@/utils/navigation';
import {
  Box,
  Button,
  Icon,
  Label,
  List,
  ListItem,
  PlainButton,
  PlainLink,
  PopoverContent,
  Search,
  Text,
  TextLabel,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import {
  FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  ENTRANCE_FADE_UP_ANIMATE,
  entranceFadeUpInitial,
  FAST_01_DURATION,
  FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
  REDUCED_MOTION_TRANSITION,
} from '@/utils/motion';

import type { Ref } from 'react';
import { use, useMemo, useRef, useState } from 'react';
import { WorkspaceDot } from '../../../workspace-dot';
import * as styles from './index.css';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAuth } from '@/services/auth';
import { useStore } from '@nanostores/react';
import {
  changeToSession,
  currentSessionStore,
  type Session,
  sessionsStore,
  updateSessions,
} from '@customerio/hydra-session-state';
import { MAX_ACCOUNTS, PENDING_SESSION_SWITCH_KEY, AccountSwitcherContext } from '@/features/account-switcher';
import { useServicesMutation } from '@/services/api';

const PANEL_SLIDE_DISTANCE = 160;

const panelVariants = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? PANEL_SLIDE_DISTANCE : -PANEL_SLIDE_DISTANCE,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? -PANEL_SLIDE_DISTANCE : PANEL_SLIDE_DISTANCE,
    opacity: 0,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

function itemStaggerTransition(index: number, shouldReduceMotion: boolean | null) {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  return {
    ...FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
    delay: Math.min(index, 5) * FAST_01_DURATION,
  };
}

function isWorkspaceActive(
  workspace: MergedWorkspace,
  currentWorkspace: MergedWorkspace | null,
  activeApp: AppBundle | null,
) {
  if (activeApp === AppBundle.Journeys) {
    return workspace.cio_workspace_id === currentWorkspace?.cio_workspace_id;
  }

  return workspace.id === currentWorkspace?.id;
}

function isSessionExpired(session: Session): boolean {
  return session.expires_at < Math.floor(Date.now() / 1000);
}

function SampleWorkspaceItem() {
  return (
    <Tooltip
      content="Explore Customer.io with sample data — view only, no changes allowed."
      placement="right"
      isCustom={true}
    >
      {(context) => (
        <PlainLink
          href="/sample-workspace"
          className={styles.workspaceItemLink}
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          <div className={styles.workspaceItemNameContainer}>
            <Icon name="a-b-test" size="sm" />
            <Truncated className={styles.workspaceItemName} aria-label="Sample Workspace">
              Sample Workspace
            </Truncated>
          </div>
        </PlainLink>
      )}
    </Tooltip>
  );
}

function useIsWorkspaceActive(workspace: MergedWorkspace) {
  const currentWorkspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  return isWorkspaceActive(workspace, currentWorkspace, activeApp);
}

function WorkspaceDropdownWorkspace(props: {
  workspace: MergedWorkspace;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const { workspace, onWorkspaceLinkClick } = props;
  const currentWorkspace = useCurrentWorkspace();
  const topLevelTransition: TargetTransition = {
    url: workspaceUrlFromCurrentPath(workspace.id, window.location.pathname),
    options: {
      shouldFadeOut: workspace.id !== currentWorkspace?.id,
      shouldUnrenderOutlet: false,
    },
  };

  return (
    <a
      href={topLevelTransition.url}
      className={styles.workspaceItemLink}
      onClick={(event) => {
        onWorkspaceLinkClick(event, topLevelTransition.options);
      }}
    >
      <div className={styles.workspaceItemNameContainer}>
        {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}
        <Truncated className={styles.workspaceItemName} aria-label={`${workspace.name} workspace`}>
          {workspace.name}
        </Truncated>
      </div>
    </a>
  );
}

function WorkspaceStaggerItem(props: {
  workspace: MergedWorkspace;
  index: number;
  shouldReduceMotion: boolean | null;
  hasToggled: boolean;
  isSampleWorkspace: boolean;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
  currentWorkspaceRef: Ref<HTMLLIElement> | null;
}) {
  const isActive = useIsWorkspaceActive(props.workspace);

  return (
    <m.li
      className={`${styles.staggerItem} ${styles.workspaceItem}`}
      aria-current={isActive}
      ref={props.currentWorkspaceRef}
      initial={props.hasToggled ? entranceFadeUpInitial(props.shouldReduceMotion) : false}
      animate={ENTRANCE_FADE_UP_ANIMATE}
      transition={props.hasToggled ? itemStaggerTransition(props.index, props.shouldReduceMotion) : undefined}
    >
      {props.isSampleWorkspace ? (
        <SampleWorkspaceItem />
      ) : (
        <WorkspaceDropdownWorkspace workspace={props.workspace} onWorkspaceLinkClick={props.onWorkspaceLinkClick} />
      )}
    </m.li>
  );
}

function AccountListItemContent(props: { isExpired: boolean; session: Session }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap="100" w="full">
      <Box minW="0">
        <Truncated display="block" text="product-p-active">
          {props.session.name}
        </Truncated>
        <Truncated display="block" text="product-pxs" color="subtle">
          {props.session.email}
        </Truncated>
      </Box>
      {props.isExpired ? (
        <Label color="grey" showStatusIndicator={true}>
          Signed out
        </Label>
      ) : null}
    </Box>
  );
}

function AccountListItem(props: {
  session: Session;
  onSwitchAccount: (sessionId: number) => unknown;
  onLogoutAccount: (sessionId: number) => unknown;
  isLoggingOut: boolean;
}) {
  const { session, onSwitchAccount, onLogoutAccount, isLoggingOut } = props;
  const isExpired = isSessionExpired(session);
  const sharedProps = {
    flexGrow: '1',
    minW: '0',
    px: '200',
    py: '150',
    textAlign: 'left',
  } as const;

  return (
    <>
      {isExpired ? (
        <PlainLink
          {...sharedProps}
          href="/login?addAccount=true"
          isExternal
          withSafeExternalAttributes={false}
          aria-label={`${session.name} account (expired)`}
          display="block"
        >
          <AccountListItemContent isExpired={isExpired} session={session} />
        </PlainLink>
      ) : (
        <PlainButton
          {...sharedProps}
          onClick={() => onSwitchAccount(session.id)}
          aria-label={`${session.name} account`}
        >
          <AccountListItemContent isExpired={isExpired} session={session} />
        </PlainButton>
      )}
      <Button
        variant="subtle"
        className={styles.sessionItemLogoutButton}
        onClick={() => onLogoutAccount(session.id)}
        isDisabled={isLoggingOut}
        aria-label={`Log out of ${session.name} account`}
        icon="delete-trash"
        isIconOnly={true}
        size="sm"
      />
    </>
  );
}

function SwitchAccountButton(props: { onClick: () => void }) {
  const { isSwitchingAccounts } = use(AccountSwitcherContext);
  const currentSession = useStore(currentSessionStore);

  if (currentSession == null) {
    return null;
  }

  return (
    <PlainButton
      onClick={props.onClick}
      display="flex"
      w="full"
      justifyContent="space-between"
      alignItems="center"
      textAlign="left"
      px="200"
      py="150"
      className={styles.accountSwitcherButton}
      aria-expanded={isSwitchingAccounts}
      aria-controls={isSwitchingAccounts ? 'account-switcher-accounts-list' : undefined}
      aria-label="Switch accounts"
    >
      <Box>
        <Text display="block" text="product-p-active">
          {currentSession.name}
        </Text>
        <Text display="block" text="product-pxs-secondary">
          {currentSession.email}
        </Text>
      </Box>
      <Icon className={styles.accountSwitcherButtonIcon} name="chevron-right" />
    </PlainButton>
  );
}

export function WorkspaceDropdown(props: {
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const auth = useAuth();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [hasToggled, setHasToggled] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const workspaceCountQuery = useWorkspaceCount();
  const { isSampleWorkspace } = useSampleWorkspace();
  const filteredWorkspacesQuery = useFilterWorkspacesByName(searchQuery, {
    includeSampleWorkspace: true,
  });

  const { isSwitchingAccounts, setIsSwitchingAccounts } = use(AccountSwitcherContext);
  const currentWorkspace = useCurrentWorkspace();

  const canSwitchAccounts = !auth.account.is_masq;
  const sessions = useStore(sessionsStore);
  const currentSession = useStore(currentSessionStore);
  const snackbars = useSnackbars();

  const filteredSessions = useMemo(() => {
    if (isEmptyString(searchQuery)) {
      return sessions;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return sessions.filter((session) => `${session.name}|${session.email}`.toLowerCase().includes(lowerQuery));
  }, [searchQuery, sessions]);

  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const shouldShowSearch = isSwitchingAccounts ? sessions.length > 3 : allWorkspacesCount > 3;

  const currentWorkspaceElementRef = useRef<HTMLLIElement | null>(null);

  useMountEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  });

  const { track } = useAnalytics();

  const handleToggleAccounts = () => {
    setHasToggled(true);
    setDirection(isSwitchingAccounts ? -1 : 1);
    if (!isSwitchingAccounts) {
      void track('multiAccount', 'viewedAccounts', { 'Session Count': sessions.length });
    }
    setIsSwitchingAccounts(!isSwitchingAccounts);
    setSearchQuery('');
  };

  const logoutSessionMutation = useServicesMutation('logoutSession');

  const handleLogoutAccount = async (sessionId: number) => {
    const targetSession = sessions.find((s) => s.id === sessionId);
    if (targetSession == null) {
      return;
    }

    try {
      await logoutSessionMutation.mutateAsync({
        params: { id: sessionId },
        body: {},
      });
    } catch {
      snackbars.create({
        id: 'multi-account.account.logout.error',
        message: "Couldn't log out of that account. Please try again",
        isError: true,
      });
      return;
    }

    void track('multiAccount', 'loggedOutAccount', {
      'Session Count': sessions.length,
      'Logged-Out Account Name': targetSession.name,
      'Was Current': sessionId === currentSession?.id,
    });

    const remaining = sessions.filter((session) => session.id !== sessionId);
    updateSessions(remaining);

    if (sessionId !== currentSession?.id) {
      return;
    }

    if (remaining.length === 0) {
      window.location.assign('/logout');
      return;
    }

    // Switch to the next available session (lowest index in what remains).
    const next = remaining[0];
    changeToSession(next.id);
    setIsSwitchingAccounts(false);
    window.location.assign('/');
  };

  const handleSwitchAccount = (sessionId: number) => {
    if (sessionId === currentSession?.id) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }

    // Should not happen in the real-world
    const targetSession = sessions.find((s) => s.id === sessionId);
    if (targetSession == null) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }

    void track('multiAccount', 'switchedAccounts', {
      'Session Count': sessions.length,
      'Current Account ID': currentSession?.id,
      'New Account Name': targetSession.name,
    });

    // To handle any errors after the workspace switch (e.g. the session expired
    // and we didn't know) we want to be able to show an informative snackbar to
    // the user and allow them to log in again.
    sessionStorage.setItem(
      PENDING_SESSION_SWITCH_KEY,
      JSON.stringify({ id: targetSession.id, name: targetSession.name }),
    );
    changeToSession(sessionId);
    setIsSwitchingAccounts(false);
    // Navigate to the dashboard for the new account. Here we don't want the
    // workspace or account ID in the URL, and we also want the page to fully
    // load, rather than navigate internally, so that fresh data is loaded.
    window.location.assign('/');
  };

  return (
    <PopoverContent className={styles.content}>
      {canSwitchAccounts ? <SwitchAccountButton onClick={handleToggleAccounts} /> : null}
      <Box px="200" py="200">
        {shouldShowSearch ? (
          <Search
            value={searchQuery}
            onSearchInput={setSearchQuery}
            onSearch={setSearchQuery}
            shouldSearchOnClear={true}
            shouldSearchOnInput={true}
            placeholder={isSwitchingAccounts ? 'Find an account…' : 'Find a workspace…'}
            autoFocus={true}
            label={isSwitchingAccounts ? 'Accounts' : 'Workspaces'}
            w="full"
            maxW="full"
          />
        ) : (
          <TextLabel>{isSwitchingAccounts ? 'Accounts' : 'Workspaces'}</TextLabel>
        )}
      </Box>
      <m.div className={styles.panelWrapper}>
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          {isSwitchingAccounts ? (
            <m.section
              key="accounts"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <section className={styles.workspaceList}>
                <List id="account-switcher-accounts-list" styleType="none" gap="0">
                  {filteredSessions.length > 0 ? (
                    filteredSessions.map((session, index) => (
                      <m.li
                        key={session.email}
                        className={styles.sessionItem}
                        aria-current={currentSession?.id === session.id}
                        initial={hasToggled ? entranceFadeUpInitial(shouldReduceMotion) : false}
                        animate={ENTRANCE_FADE_UP_ANIMATE}
                        transition={hasToggled ? itemStaggerTransition(index, shouldReduceMotion) : undefined}
                      >
                        <AccountListItem
                          session={session}
                          onSwitchAccount={handleSwitchAccount}
                          onLogoutAccount={handleLogoutAccount}
                          isLoggingOut={
                            logoutSessionMutation.isPending &&
                            logoutSessionMutation.variables?.params?.id === session.id
                          }
                        />
                      </m.li>
                    ))
                  ) : (
                    <ListItem>
                      <TextLabel m="200" variant="secondary">
                        No results found
                      </TextLabel>
                    </ListItem>
                  )}
                </List>
              </section>
              <Box as="section" display="flex" alignItems="center" py="150" px="200">
                <Button
                  flexGrow="1"
                  isDisabled={sessions.length >= MAX_ACCOUNTS}
                  // Navigating internally to the login page causes issues with
                  // form field tracking. Until we can figure out why, we're
                  // going to do a full reload. Not ideal, but a temporary solution
                  href="/login?addAccount=true"
                  isExternal={true}
                  withSafeExternalAttributes={false}
                  onClick={() => {
                    void track('multiAccount', 'clickedAddAccount', { 'Session Count': sessions.length });
                  }}
                >
                  Add another account
                </Button>
              </Box>
            </m.section>
          ) : (
            <m.div
              key="workspaces"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <section className={styles.workspaceList}>
                <List styleType="none" gap="0">
                  {workspaceData.length > 0 ? (
                    workspaceData.map((w: MergedWorkspace, index: number) => (
                      <WorkspaceStaggerItem
                        key={w.id}
                        workspace={w}
                        index={index}
                        shouldReduceMotion={shouldReduceMotion}
                        hasToggled={hasToggled}
                        isSampleWorkspace={isSampleWorkspace(w.cio_workspace_id)}
                        onWorkspaceLinkClick={props.onWorkspaceLinkClick}
                        currentWorkspaceRef={w.id === currentWorkspace?.id ? currentWorkspaceElementRef : null}
                      />
                    ))
                  ) : (
                    <ListItem>
                      <TextLabel m="200" variant="secondary">
                        No results found
                      </TextLabel>
                    </ListItem>
                  )}
                </List>
              </section>
              <Box as="section" display="flex" alignItems="center" py="150" px="200">
                <Button
                  href="/settings/workspaces"
                  flexGrow="1"
                  onClick={(event) => {
                    props.onWorkspaceLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
                  }}
                >
                  Manage all workspaces
                </Button>
              </Box>
            </m.div>
          )}
        </AnimatePresence>
      </m.div>
    </PopoverContent>
  );
}
